import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, Flex, Form, Input, Modal, Row, Segmented, Space, Statistic, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { CLIENT_PAGING, useDateRange, useListPage } from "@/components/list/useListPage";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter, SearchFilter, useSearchFilter } from "@/components/list/filters";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { ConditionTag, type Condition } from "../inventory/conditions";
import { NoWms, useWmsWarehouse } from "./common";
import { parseSerials } from "./SerialImport";

type Row = Schemas["VarianceRow"];
type Writeoff = Schemas["Writeoff"];
const rowKey = (r: Row) => `${r.skuId}/${r.lotId ?? ""}/${r.condition}`;

/**
 * Gudang > Selisih (Plan §6.9): what counts did not find (+) or found extra
 * (−), waiting in the variance location. Nothing here is a loss or a gain
 * until it is confirmed.
 */
export function Variance() {
  const { t, formatNumber, formatDateTime, formatMoney } = useI18n();
  const { me, can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const { current, loading } = useWmsWarehouse();
  const [selected, setSelected] = useState<string[]>([]);
  const [confirming, setConfirming] = useState<Row[] | null>(null);
  const tz = me?.workspace?.timezone;
  const manage = can("inventory.manage");
  // Waiting to be confirmed, or Riwayat selisih (what was confirmed).
  const [tab, setTab] = useUrlFilter<"open" | "history">("tab", "open", ["open", "history"]);

  const list = useQuery({
    queryKey: ["variance", current?.id],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/variance", { params: { path: { warehouseID: current!.id } } })),
    enabled: !!current,
  });
  const rows = list.data?.items ?? [];
  const missing = rows.filter((r) => r.quantity > 0).reduce((n, r) => n + r.quantity, 0);
  const found = rows.filter((r) => r.quantity < 0).reduce((n, r) => n - r.quantity, 0);
  // Worth at average cost: only those who see finance get unitCost.
  const costed = rows.some((r) => r.unitCost != null);
  const missingValue = rows.filter((r) => r.quantity > 0).reduce((n, r) => n + r.quantity * (r.unitCost ?? 0), 0);
  const foundValue = rows.filter((r) => r.quantity < 0).reduce((n, r) => n - r.quantity * (r.unitCost ?? 0), 0);
  // An SKU missing in one lot and found in another (same condition) can be netted.
  const nettable = new Set(
    rows
      .filter((r) => r.quantity > 0 && rows.some((x) => x.skuId === r.skuId && x.condition === r.condition && x.quantity < 0))
      .map((r) => `${r.skuId}/${r.condition}`),
  );

  const netOff = useMutation({
    mutationFn: (r: Row) => unwrap(api.POST("/v1/inventory/variance/net-off", { body: { warehouseId: current!.id, skuId: r.skuId, condition: r.condition } })),
    onSuccess: (res) => {
      void message.success(t("wms.var.netted", { n: res.units }));
      void queryClient.invalidateQueries({ queryKey: ["variance"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
    },
    onError: (err) => void message.error(errorText(err)),
  });

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space size={16} wrap>
          <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
            {t("wms.var.title")}
          </Typography.Title>
          <Segmented
            value={tab}
            onChange={setTab}
            options={[
              { value: "open", label: t("wms.var.tab.open") },
              { value: "history", label: t("wms.var.tab.history") },
            ]}
          />
        </Space>
        {manage && tab === "open" && (
          <Button type="primary" disabled={selected.length === 0} onClick={() => setConfirming(rows.filter((r) => selected.includes(rowKey(r))))}>
            {t("wms.var.confirm", { n: selected.length })}
          </Button>
        )}
      </Flex>
      {tab === "history" && current ? (
        <VarianceHistory warehouseId={current.id} />
      ) : (
      <>
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("wms.var.help")} />
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("wms.var.missing")} value={missing} formatter={(v) => formatNumber(Number(v))} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("wms.var.found")} value={found} formatter={(v) => formatNumber(Number(v))} />
          </Card>
        </Col>
        {costed && (
          <>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title={t("wms.var.missingValue")} value={formatMoney(missingValue)} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title={t("wms.var.foundValue")} value={formatMoney(foundValue)} />
              </Card>
            </Col>
          </>
        )}
      </Row>
      <Table<Row>
        rowKey={rowKey}
        loading={list.isPending}
        dataSource={rows}
        pagination={CLIENT_PAGING}
        locale={{ emptyText: t("wms.var.empty") }}
        rowSelection={manage ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) } : undefined}
        columns={[
          {
            title: t("catalog.code"),
            dataIndex: "skuCode",
            render: (v: string, r) => (
              <Space orientation="vertical" size={0}>
                <span className="z-num">{v}</span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {r.skuName}
                </Typography.Text>
              </Space>
            ),
          },
          { title: t("inventory.bin.lot"), dataIndex: "lotNo", width: 160, render: (v: string | null, r) => (v ? `${v}${r.expiresOn ? ` · ${r.expiresOn}` : ""}` : "—") },
          { title: t("inventory.condition"), dataIndex: "condition", width: 130, render: (v: Condition) => <ConditionTag condition={v} /> },
          {
            title: t("wms.var.quantity"),
            dataIndex: "quantity",
            width: 170,
            align: "right",
            render: (v: number) =>
              v > 0 ? (
                <Tag color="warning" className="z-num">{t("wms.var.missingN", { n: formatNumber(v) })}</Tag>
              ) : (
                <Tag color="processing" className="z-num">{t("wms.var.foundN", { n: formatNumber(-v) })}</Tag>
              ),
          },
          ...(costed
            ? [
                {
                  title: t("wms.var.value"),
                  width: 150,
                  align: "right" as const,
                  render: (_: unknown, r: Row) => (r.unitCost != null ? <span className="z-num">{formatMoney(Math.abs(r.quantity) * r.unitCost)}</span> : "—"),
                },
              ]
            : []),
          {
            title: t("wms.var.serials"),
            dataIndex: "serialsNeeded",
            width: 170,
            render: (n: number, r: Row) => (r.tracking === "serial" && n > 0 ? <Tag color="warning">{t("serial.var.needed", { n })}</Tag> : "—"),
          },
          { title: t("wms.var.lastAt"), dataIndex: "lastAt", width: 170, render: (v: string) => formatDateTime(v, tz) },
          ...(manage
            ? [
                {
                  width: 130,
                  render: (_: unknown, r: Row) =>
                    r.quantity > 0 &&
                    nettable.has(`${r.skuId}/${r.condition}`) && (
                      <Button size="small" loading={netOff.isPending && netOff.variables === r} onClick={() => netOff.mutate(r)}>
                        {t("wms.var.netOff")}
                      </Button>
                    ),
                },
              ]
            : []),
        ]}
      />
      {current && confirming && (
        <ConfirmModal
          warehouseId={current.id}
          rows={confirming}
          onClose={(done) => {
            setConfirming(null);
            if (done) setSelected([]);
          }}
        />
      )}
      </>
      )}
    </>
  );
}

function ConfirmModal({ warehouseId, rows, onClose }: { warehouseId: string; rows: Row[] | null; onClose: (done: boolean) => void }) {
  const { t, formatNumber, formatMoney } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ reason: string }>();
  // Serial SKUs: the IMEIs missing or found, one box per row that needs some.
  const [serials, setSerials] = useState<Record<string, string>>({});
  const keyOf = (r: Row) => `${r.skuId}/${r.lotId ?? ""}/${r.condition}`;
  const serialRows = (rows ?? []).filter((r) => r.tracking === "serial" && r.serialsNeeded > 0);
  const listOf = (r: Row) => parseSerials(serials[keyOf(r)] ?? "");
  const serialsReady = serialRows.every((r) => listOf(r).length === r.serialsNeeded);
  const loss = (rows ?? []).filter((r) => r.quantity > 0).reduce((n, r) => n + r.quantity, 0);
  const gain = (rows ?? []).filter((r) => r.quantity < 0).reduce((n, r) => n - r.quantity, 0);
  const costed = (rows ?? []).some((r) => r.unitCost != null);
  const net = (rows ?? []).reduce((n, r) => n - r.quantity * (r.unitCost ?? 0), 0);
  const save = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        api.POST("/v1/inventory/variance/confirm", {
          body: {
            warehouseId,
            reason,
            items: rows!.map((r) => ({
              skuId: r.skuId,
              lotId: r.lotId,
              condition: r.condition,
              quantity: r.quantity,
              ...(r.tracking === "serial" && r.serialsNeeded > 0 ? { serials: listOf(r) } : {}),
            })),
          },
        }),
      ),
    onSuccess: (res) => {
      void message.success(t("wms.var.confirmed", { loss: formatNumber(res.loss), gain: formatNumber(res.gain) }));
      void queryClient.invalidateQueries({ queryKey: ["variance"] });
      void queryClient.invalidateQueries({ queryKey: ["inventory"] });
      onClose(true);
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={rows !== null}
      title={t("wms.var.confirmTitle")}
      okText={t("wms.var.confirmOk")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      okButtonProps={{ disabled: !serialsReady }}
      onOk={() => form.submit()}
      onCancel={() => onClose(false)}
      destroyOnHidden
    >
      <Typography.Paragraph>{t("wms.var.confirmBody", { loss: formatNumber(loss), gain: formatNumber(gain) })}</Typography.Paragraph>
      {costed && <Typography.Paragraph strong>{t("wms.var.confirmValue", { value: formatMoney(net) })}</Typography.Paragraph>}
      {serialRows.map((r) => (
        <div key={keyOf(r)} style={{ marginBottom: 12 }}>
          <Typography.Text strong className="z-num">
            {r.skuCode}
          </Typography.Text>{" "}
          <Typography.Text type="secondary">
            {t(r.quantity > 0 ? "serial.var.missingHelp" : "serial.var.foundHelp", { n: r.serialsNeeded })} ({listOf(r).length}/{r.serialsNeeded})
          </Typography.Text>
          <Input.TextArea
            rows={3}
            className="z-num"
            value={serials[keyOf(r)] ?? ""}
            onChange={(e) => setSerials((s) => ({ ...s, [keyOf(r)]: e.target.value }))}
            placeholder={t("serial.var.placeholder")}
          />
        </div>
      ))}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => save.mutate(v.reason.trim())}>
        <Form.Item name="reason" label={t("inventory.reason")} rules={[{ required: true, whitespace: true, message: t("field.required") }]}>
          <Input.TextArea rows={2} maxLength={300} placeholder={t("wms.var.reasonPlaceholder")} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** Riwayat selisih: every confirmed difference, with what it was worth. */
function VarianceHistory({ warehouseId }: { warehouseId: string }) {
  const { t, formatNumber, formatDateTime, formatMoney } = useI18n();
  const { me, can } = useAuth();
  const tz = me?.workspace?.timezone;
  const search = useSearchFilter();
  const range = useDateRange();
  const page = useListPage();
  const finance = can("finance.view");
  const filters = { ...search.query, ...range.query };
  const list = useQuery({
    queryKey: ["variance", "history", warehouseId, filters, page.query],
    queryFn: () =>
      unwrap(api.GET("/v1/warehouses/{warehouseID}/variance/history", { params: { path: { warehouseID: warehouseId }, query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("wms.var.historySearch")} />
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path={`/v1/warehouses/${warehouseId}/variance/history/export`}
          query={filters}
          filename={t("wms.var.tab.history")}
          columns={[
            { key: "at", label: t("wms.var.historyAt"), kind: "datetime" },
            { key: "skuCode", label: t("catalog.code") },
            { key: "skuName", label: t("common.name") },
            { key: "lotNo", label: t("inventory.bin.lot") },
            { key: "condition", label: t("inventory.condition") },
            { key: "quantity", label: t("wms.var.historyQty"), kind: "number" },
            ...(finance
              ? [
                  { key: "unitCost", label: t("wms.var.unitCost"), kind: "number" as const },
                  { key: "value", label: t("wms.var.value"), kind: "number" as const },
                ]
              : []),
            { key: "reason", label: t("inventory.reason") },
            { key: "actorName", label: t("wms.var.historyBy") },
          ]}
        />
      </Flex>
      <Table<Writeoff>
        rowKey="id"
        loading={list.isFetching}
        dataSource={list.data?.items}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("wms.var.historyEmpty") }}
        columns={[
          { title: t("wms.var.historyAt"), dataIndex: "at", width: 170, render: (v: string) => formatDateTime(v, tz) },
          {
            title: t("catalog.code"),
            dataIndex: "skuCode",
            render: (v: string, w) => (
              <Space orientation="vertical" size={0}>
                <span className="z-num">
                  {v}
                  {w.lotNo ? ` · ${w.lotNo}` : ""}
                </span>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {w.skuName}
                </Typography.Text>
              </Space>
            ),
          },
          { title: t("inventory.condition"), dataIndex: "condition", width: 120, render: (c: Condition) => <ConditionTag condition={c} /> },
          {
            title: t("wms.var.historyQty"),
            dataIndex: "quantity",
            width: 120,
            align: "right",
            render: (q: number) => (
              <Typography.Text className="z-num" type={q > 0 ? "danger" : "success"}>
                {q > 0 ? t("wms.var.lost", { n: formatNumber(q) }) : t("wms.var.foundN", { n: formatNumber(-q) })}
              </Typography.Text>
            ),
          },
          ...(finance
            ? [
                {
                  title: t("wms.var.value"),
                  width: 160,
                  align: "right" as const,
                  render: (_: unknown, w: Writeoff) =>
                    w.value == null ? (
                      <Typography.Text type="secondary">{t("wms.var.noCost")}</Typography.Text>
                    ) : (
                      <Typography.Text className="z-num" type={w.value < 0 ? "danger" : "success"}>
                        {formatMoney(w.value)}
                      </Typography.Text>
                    ),
                },
              ]
            : []),
          {
            title: t("inventory.reason"),
            dataIndex: "reason",
            render: (v: string, w) => (
              <Space size={4}>
                {w.kind === "adjustment" && <Tag>{t("inventory.reason.adjustment")}</Tag>}
                {v || "—"}
              </Space>
            ),
          },
          { title: t("wms.var.historyBy"), dataIndex: "actorName", width: 150 },
        ]}
      />
    </>
  );
}
